import React from 'react';
import { AlertTriangle, Plus, Trash2 } from 'lucide-react';

/**
 * Club_CartFooter — cuentas y acciones del carrito del POS.
 *
 * Contrapartida en código del componente de Figma (3155:11495), con sus dos
 * variantes de la propiedad `Credit`:
 *
 *   Credit=Yes   el saldo cubre el total
 *   Credit=No    no lo cubre: el socio queda debiendo
 *
 * IMPORTANTE — el frame original BLOQUEABA la venta en `Credit=No`: «Validar
 * compra» salía apagado y debajo ponía «Añade crédito para poder validar». Esa
 * regla no existe en el club: se puede retirar sin saldo y, por ahora, sin
 * límite de deuda. El código ya lo hacía bien (`canCheckout` solo mira que el
 * carrito no esté vacío); el diseño se corrigió para que coincida, 18-ago.
 *
 * Reparto de papeles, para no decir dos veces lo mismo:
 *   · la FILA «Saldo tras compra» da el dato, en rojo si es negativo
 *     (convención contable, igual que la ficha del socio);
 *   · la BANDA dice que aun así se puede cobrar y ofrece añadir crédito.
 * Sin la banda, un −€39,24 en rojo se lee como error; sin la fila, la banda
 * tendría que repetir la cifra.
 */

/**
 * El signo va DELANTE del símbolo de moneda: «−€32.00», no «€-32.00». Es como
 * lo pone el frame y como se lee un importe negativo en cualquier extracto.
 */
export function eur(n) {
  const v = Number(n) || 0;
  return `${v < 0 ? '−' : ''}€${Math.abs(v).toFixed(2)}`;
}

function Linea({ etiqueta, children, destacado = false }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span
        className={
          destacado
            ? 'text-sm font-bold text-[var(--foreground)]'
            : 'text-xs text-[var(--muted-foreground)]'
        }
      >
        {etiqueta}
      </span>
      {children}
    </div>
  );
}

export default function CartFooter({
  subtotal = 0,
  count = 0,
  /** Saldo del socio antes de cobrar. `null` si la compra no tiene socio. */
  balance = null,
  /**
   * Descuentos aplicados, como `[{ label, amount }]`. Van en lista y no como una
   * cifra única porque el POS distingue el del socio (su cuota) del manual de
   * cada línea, y el staff necesita ver cuál es cuál.
   */
  discounts = [],
  total = 0,
  onCheckout,
  onClear,
  onContinue,
  onAddCredit,
  /** Deshabilita solo lo que de verdad impide cobrar: un carrito vacío. */
  disabled = false,
  /** El frame lleva 16 de padding; se quita cuando el panel que lo envuelve ya acolcha. */
  padded = true,
  /**
   * Pega las acciones al borde inferior visible. En la tablet apaisada del club
   * un carrito largo empujaba «Validar compra» fuera de alcance (Kong House,
   * 08-2026) — no quitar sin reproducirlo en pantalla corta.
   */
  stickyActions = false,
  className = '',
}) {
  const saldo = Number(balance) || 0;
  const tras = saldo - Number(total);
  const negativo = balance != null && tras < 0;

  const importe = (n, extra = '') => (
    <span className={`text-xs font-bold tabular-nums ${extra || 'text-[var(--foreground)]'}`}>
      {eur(n)}
    </span>
  );

  return (
    <div
      data-testid="cart-footer"
      className={`flex flex-col gap-4 ${padded ? 'p-4' : ''} ${className}`}
    >
      <div className="flex flex-col gap-1">
        <Linea etiqueta={`Subtotal (${count} ${count === 1 ? 'producto' : 'productos'})`}>
          {importe(subtotal)}
        </Linea>

        {discounts
          .filter((d) => Number(d.amount) > 0)
          .map((d) => (
            <Linea key={d.label} etiqueta={d.label}>
              <span className="text-xs font-bold tabular-nums text-[var(--success)]">
                {eur(-Math.abs(Number(d.amount)))}
              </span>
            </Linea>
          ))}

        {balance != null && (
          <>
            <Linea etiqueta="Saldo">
              {importe(saldo, 'text-[var(--muted-foreground)]')}
            </Linea>
            <Linea etiqueta="Saldo tras compra">
              {importe(
                tras,
                negativo ? 'text-[var(--destructive)]' : 'text-[var(--muted-foreground)]'
              )}
            </Linea>
          </>
        )}
      </div>

      <div className="border-t border-[var(--border)] pt-3">
        <Linea etiqueta="Total" destacado>
          <span className="text-sm font-bold tabular-nums text-[var(--foreground)]">
            {eur(total)}
          </span>
        </Linea>
      </div>

      {negativo && (
        <div
          data-testid="cart-negative-notice"
          className="flex gap-3 rounded-lg border border-[var(--toast-warning-border)] bg-[var(--toast-warning-bg)] p-3"
        >
          <AlertTriangle
            size={16}
            strokeWidth={1.5}
            className="mt-0.5 flex-shrink-0 text-[var(--toast-warning-icon)]"
          />
          <div className="min-w-0">
            <div className="text-sm font-bold text-[var(--toast-warning-text)]">
              Se puede cobrar igualmente
            </div>
            <div className="mt-0.5 text-xs text-[var(--warning)]">
              La deuda queda pendiente hasta que el socio recargue.
            </div>
            {onAddCredit && (
              <button
                type="button"
                onClick={onAddCredit}
                className="mt-2 inline-flex items-center gap-1 rounded-sm bg-[var(--toast-warning-border)] px-3 py-1.5 text-xs font-bold text-[var(--toast-warning-text)] transition-opacity hover:opacity-88"
              >
                <Plus size={12} strokeWidth={2} />
                Añadir crédito al socio
              </button>
            )}
          </div>
        </div>
      )}

      <div
        data-testid="cart-actions"
        className={`flex flex-col gap-3 ${
          stickyActions
            ? 'sticky bottom-0 z-10 -mx-3 border-t border-[var(--border)] bg-[var(--background)] px-3 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:-mx-4 sm:px-4'
            : ''
        }`}
      >
        <button
          type="button"
          onClick={onCheckout}
          disabled={disabled}
          title={disabled ? 'Carrito vacío' : undefined}
          className="h-9 w-full rounded-sm bg-[var(--primary)] text-sm font-medium text-[var(--primary-foreground)] transition-colors hover:bg-[var(--primary-strong)] disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-[var(--primary)]"
        >
          Validar compra
        </button>

        <div className="flex gap-2">
          <button
            type="button"
            onClick={onClear}
            className="inline-flex h-9 flex-1 items-center justify-center gap-2 rounded-sm bg-[var(--destructive-bg)] text-sm font-medium text-[var(--destructive)] transition-colors hover:bg-[var(--destructive-border)]"
          >
            <Trash2 size={16} strokeWidth={1.5} />
            Vaciar carrito
          </button>
          <button
            type="button"
            onClick={onContinue}
            className="inline-flex h-9 flex-1 items-center justify-center gap-2 rounded-sm border border-[var(--border)] bg-[var(--background)] text-sm font-medium text-[var(--foreground)] transition-colors hover:border-[var(--border-hover)]"
          >
            <Plus size={16} strokeWidth={1.5} />
            Seguir añadiendo
          </button>
        </div>
      </div>
    </div>
  );
}
